/**
 * Pagina /products — lista prodotti + form creazione.
 *
 * Visibilità:
 *  - Tabella: tutti gli utenti autenticati (VIEW_PORTFOLIO).
 *  - Form "Nuovo prodotto": solo chi ha PORTFOLIO_MANAGEMENT (developer).
 *
 * Gating UI tramite usePermissions().can('PORTFOLIO_MANAGEMENT').
 */

import { ModalPortal } from '../../components/ModalPortal.js';
import { useState, type FormEvent, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../../auth/index.js';
import {
  useProducts,
  useCreateProduct,
  useDeleteProduct,
  useOpenVulnSets,
  usePendingScanSets,
  useProductNextSlaMap,
} from './hooks.js';
import { fetchDeployments } from './api.js';
import { ApiError } from '../../api/client.js';
import type { CreateProductInput, CraCategory, Product } from './types.js';
import { PendingScanBadge } from './PendingScanBadge.js';
import { formatSlaHours } from './productNextSla.js';
import {
  buildComparator,
  toggleSort,
  type ProductSortKey,
  type SortCriterion,
  type SortValue,
} from './productSort.js';
import styles from './Products.module.css';

/** Parti CPE 2.3 (`part`): mappano l'etichetta utente sul carattere a|o|h. */
const CPE_PART_OPTIONS = [
  { value: 'a', label: 'Application' },
  { value: 'o', label: 'OS' },
  { value: 'h', label: 'Hardware' },
] as const;

/** Categorie CRA del prodotto. */
const CRA_CATEGORY_OPTIONS = [
  { value: 'default' as const, label: 'Default' },
  { value: 'important' as const, label: 'Important' },
  { value: 'critical' as const, label: 'Critical' },
] as const;

type CpePart = (typeof CPE_PART_OPTIONS)[number]['value'];

/**
 * Compone la testa CPE 2.3 `cpe:2.3:<part>:<vendor>:<product>` dai campi separati.
 * Ritorna undefined se vendor e product sono entrambi vuoti → prodotto senza CPE
 * (percorso valido, identico al vecchio campo testuale lasciato vuoto).
 */
function composeCpePrefix(part: CpePart, vendor: string, product: string): string | undefined {
  const v = vendor.trim();
  const p = product.trim();
  if (!v && !p) return undefined;
  return `cpe:2.3:${part}:${v}:${p}`;
}

/** Form di creazione prodotto: visibile solo con PORTFOLIO_MANAGEMENT. */
function NuovoProdottoForm({ onCreato }: { onCreato: () => void }) {
  const [internalId, setInternalId] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [notes, setNotes] = useState('');
  const [cpePart, setCpePart] = useState<CpePart>('a');
  const [cpeVendor, setCpeVendor] = useState('');
  const [cpeProduct, setCpeProduct] = useState('');
  const [purl, setPurl] = useState('');
  const [craCategory, setCraCategory] = useState<CraCategory>('default');

  const { mutate, isPending, isError, error } = useCreateProduct();

  // Validazione client del cpePrefix composto: il CPE è opzionale (Vendor+Product
  // entrambi vuoti → prodotto senza CPE, valido). Se invece l'utente compila qualcosa
  // deve comporre una testa CPE 2.3 completa `cpe:2.3:<part>:<vendor>:<product>`:
  // un CPE parziale (solo Vendor o solo Product) è bloccato. Stesso vincolo del
  // dominio (isValidCpePrefix) → evita un 422 dal backend.
  const cpeVendorTrim = cpeVendor.trim();
  const cpeProductTrim = cpeProduct.trim();
  const cpePrefixComposto = composeCpePrefix(cpePart, cpeVendor, cpeProduct);
  const cpePrefixValido =
    cpePrefixComposto === undefined || /^cpe:2\.3:[aoh](:[^:\s]+){2}$/.test(cpePrefixComposto);

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!cpePrefixValido) return;
    const input: CreateProductInput = {
      internalId: internalId.trim(),
      name: name.trim(),
      ...(description.trim() ? { description: description.trim() } : {}),
      ...(notes.trim() ? { notes: notes.trim() } : {}),
      ...(cpePrefixComposto ? { cpePrefix: cpePrefixComposto } : {}),
      ...(purl.trim() ? { purl: purl.trim() } : {}),
      craCategory,
    };
    mutate(input, {
      onSuccess: () => {
        setInternalId('');
        setName('');
        setDescription('');
        setNotes('');
        setCpePart('a');
        setCpeVendor('');
        setCpeProduct('');
        setPurl('');
        setCraCategory('default');
        onCreato();
      },
    });
  }

  return (
    <div data-testid="form-nuovo-prodotto">
      <form onSubmit={handleSubmit}>
        {/* Sezione 1 — Riferimenti interni: ID interno, nome, descrizione */}
        <section className={styles.formSection} data-testid="sezione-riferimenti-interni">
          <p className={styles.formSectionTitle}>Riferimenti interni</p>
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label className={styles.formLabel} htmlFor="prodotto-internalId">
                ID interno *
              </label>
              <input
                id="prodotto-internalId"
                className={styles.formInput}
                value={internalId}
                onChange={(e) => setInternalId(e.target.value)}
                required
                placeholder="es. myapp-core"
                data-testid="input-internalId"
              />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.formLabel} htmlFor="prodotto-name">
                Nome *
              </label>
              <input
                id="prodotto-name"
                className={styles.formInput}
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                placeholder="es. MyApp Core"
                data-testid="input-name"
              />
            </div>
          </div>
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label className={styles.formLabel} htmlFor="prodotto-description">
                Descrizione
              </label>
              <input
                id="prodotto-description"
                className={styles.formInput}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Descrizione opzionale"
                data-testid="input-description"
              />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.formLabel} htmlFor="prodotto-cra-category">
                Categoria CRA
              </label>
              <select
                id="prodotto-cra-category"
                className={styles.formSelect}
                value={craCategory}
                onChange={(e) => setCraCategory(e.target.value as CraCategory)}
                data-testid="select-cra-category"
              >
                {CRA_CATEGORY_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </section>

        {/* Sezione 2 — Riferimenti esterni: CPE e PURL (entrambi opzionali) */}
        <section className={styles.formSection} data-testid="sezione-riferimenti-esterni">
          <p className={styles.formSectionTitle}>Riferimenti esterni</p>

          {/* Sottosezione CPE */}
          <div className={styles.formSubSection} data-testid="sottosezione-cpe">
            <p className={styles.formSubSectionTitle}>CPE</p>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.formLabel} htmlFor="prodotto-cpe-part">
                  Part
                </label>
                <select
                  id="prodotto-cpe-part"
                  className={styles.formSelect}
                  value={cpePart}
                  onChange={(e) => setCpePart(e.target.value as CpePart)}
                  data-testid="select-cpe-part"
                >
                  {CPE_PART_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel} htmlFor="prodotto-cpe-vendor">
                  Vendor
                </label>
                <input
                  id="prodotto-cpe-vendor"
                  className={styles.formInput}
                  value={cpeVendor}
                  onChange={(e) => setCpeVendor(e.target.value)}
                  placeholder="es. adobe"
                  aria-invalid={!cpePrefixValido}
                  data-testid="input-cpe-vendor"
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel} htmlFor="prodotto-cpe-product">
                  Product
                </label>
                <input
                  id="prodotto-cpe-product"
                  className={styles.formInput}
                  value={cpeProduct}
                  onChange={(e) => setCpeProduct(e.target.value)}
                  placeholder="es. magento_open_source"
                  aria-invalid={!cpePrefixValido}
                  data-testid="input-cpe-product"
                />
              </div>
            </div>
            <div className={styles.formRow}>
              <div className={styles.formGroup} style={{ flex: 1 }}>
                <div
                  className={styles.cpePreviewBox}
                  data-invalid={cpePrefixComposto !== undefined && !cpePrefixValido}
                >
                  <span className={styles.cpePreviewLabel}>Anteprima CPE prefix</span>
                  <code className={styles.cpePreviewValue} data-testid="cpe-prefix-preview">
                    {`cpe:2.3:${cpePart}:${cpeVendorTrim || '<vendor>'}:${cpeProductTrim || '<product>'}`}
                  </code>
                </div>
                {cpePrefixComposto !== undefined && !cpePrefixValido ? (
                  <span className={styles.inlineMutError} data-testid="errore-cpe-prefix">
                    CPE prefix non valido: indicare sia Vendor sia Product, senza spazi o
                    <code>:</code>.
                  </span>
                ) : (
                  <span className={styles.formHint}>
                    La release vi concatena versione + 7 campi per il CPE completo.
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Sottosezione PURL */}
          <div className={styles.formSubSection} data-testid="sottosezione-purl">
            <p className={styles.formSubSectionTitle}>PURL</p>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.formLabel} htmlFor="prodotto-purl">
                  PURL
                </label>
                <input
                  id="prodotto-purl"
                  className={styles.formInput}
                  value={purl}
                  onChange={(e) => setPurl(e.target.value)}
                  placeholder="es. pkg:maven/..."
                  data-testid="input-purl"
                />
              </div>
            </div>
          </div>
        </section>

        <div className={styles.formActions}>
          <button
            type="submit"
            className={styles.btnPrimary}
            disabled={isPending || !cpePrefixValido}
            data-testid="btn-crea-prodotto"
          >
            {isPending ? 'Creazione…' : 'Crea prodotto'}
          </button>
          {isError && (
            <span className={styles.inlineMutError} data-testid="errore-crea-prodotto">
              {error instanceof Error ? error.message : 'Errore durante la creazione'}
            </span>
          )}
        </div>
      </form>
    </div>
  );
}

/** Modal generica: overlay scuro + pannello centrato. Chiude su backdrop, Esc o pulsante ×. */
function Modal({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  return (
    <ModalPortal onClose={onClose}>
      <div className={styles.modalOverlay} data-testid="modal-overlay" onClick={onClose}>
        <div
          className={styles.modalPanel}
          role="dialog"
          aria-modal="true"
          aria-label={title}
          data-testid="modal-nuovo-prodotto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className={styles.modalHeader}>
            <p className={styles.modalTitle}>{title}</p>
            <button
              type="button"
              className={styles.modalClose}
              onClick={onClose}
              aria-label="Chiudi"
              data-testid="btn-chiudi-modal"
            >
              ×
            </button>
          </div>
          {children}
        </div>
      </div>
    </ModalPortal>
  );
}

/**
 * Mappa la categoria CRA al suo rango di urgenza (decrescente: critical > important > default/undefined).
 * Usato come chiave di ordinamento primaria nella lista prodotti.
 */
function rangoCategoriaCra(cat: CraCategory | undefined): number {
  switch (cat) {
    case 'critical':
      return 2;
    case 'important':
      return 1;
    case 'default':
    case undefined:
      return 0;
  }
}

/**
 * Ordinamento di default: categoria CRA, poi #vulnerabilità, entrambe decrescenti. Mostrato nelle
 * intestazioni (verso + priorità) finché l'utente non sceglie un ordinamento proprio.
 */
const DEFAULT_SORT: SortCriterion[] = [
  { key: 'categoria', dir: 'desc' },
  { key: 'vuln', dir: 'desc' },
];

/**
 * Intestazione di colonna ordinabile: un click percorre asc → desc → rimossa; con più colonne
 * attive mostra la priorità (1 = criterio principale).
 */
function IntestazioneOrdinabile({
  sortKey,
  label,
  criteria,
  onToggle,
}: {
  sortKey: ProductSortKey;
  label: string;
  criteria: SortCriterion[];
  onToggle: (key: ProductSortKey) => void;
}) {
  const idx = criteria.findIndex((c) => c.key === sortKey);
  const attivo = idx !== -1 ? criteria[idx] : undefined;
  const stato = attivo
    ? `${attivo.dir === 'asc' ? 'crescente' : 'decrescente'}${
        criteria.length > 1 ? `, priorità ${idx + 1}` : ''
      }`
    : 'non ordinata';
  return (
    <button
      type="button"
      className={`${styles.sortBtn} ${attivo ? styles.sortBtnAttivo : ''}`}
      onClick={() => onToggle(sortKey)}
      aria-label={`${label}: ${stato}. Clic per cambiare l'ordinamento`}
      data-testid={`sort-${sortKey}`}
      data-sort={attivo?.dir ?? 'none'}
    >
      {label}
      <span className={styles.sortInd} aria-hidden="true">
        {attivo ? (attivo.dir === 'asc' ? '▲' : '▼') : '↕'}
        {attivo && criteria.length > 1 && (
          <sup className={styles.sortPriorita} data-testid={`sort-priorita-${sortKey}`}>
            {idx + 1}
          </sup>
        )}
      </span>
    </button>
  );
}

const FORMATO_DATA: Intl.DateTimeFormatOptions = {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
};

/** Data di creazione (gg/mm/aaaa). */
function formattaData(iso: string): string {
  return new Date(iso).toLocaleDateString('it-IT', FORMATO_DATA);
}

/** Data e ora complete, per il tooltip. */
function formattaDataOra(iso: string): string {
  return new Date(iso).toLocaleString('it-IT');
}

/** Pagina principale lista prodotti. */
export function ProductListPage() {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const { data: prodotti, isLoading, isError, error } = useProducts();
  const { data: openSets } = useOpenVulnSets();
  const { data: pendingSets } = usePendingScanSets();
  const slaMap = useProductNextSlaMap();
  const [modaleAperta, setModaleAperta] = useState(false);
  const [soloCve, setSoloCve] = useState(false);
  const [ordinamento, setOrdinamento] = useState<SortCriterion[]>([]);
  const [eliminazioneInCorso, setEliminazioneInCorso] = useState(false);
  const [erroreEliminazione, setErroreEliminazione] = useState<string | null>(null);
  const deleteProduct = useDeleteProduct();

  const puoGestire = can('PORTFOLIO_MANAGEMENT');

  // Bubble-up: per ogni prodotto, se è impattato (≥1 vulnerabilità aperta) e numero totale di
  // vulnerabilità aperte da gestire. Ordinamento: primario per categoria CRA (urgenza decrescente),
  // secondario per conteggio vulnerabilità (decrescente).
  const vulnCountByProduct = openSets?.openVulnCountByProduct;
  const eImpattato = (internalId: string): boolean => openSets?.productIds.has(internalId) ?? false;
  const vulnerabilita = (internalId: string): number => vulnCountByProduct?.get(internalId) ?? 0;
  // Valore di ciascun prodotto per ogni colonna ordinabile (undefined = assente → in fondo).
  const valoreOrdinamento = (p: Product, key: ProductSortKey): SortValue => {
    switch (key) {
      case 'vuln':
        return vulnerabilita(p.internalId);
      case 'sla':
        return slaMap?.get(p.internalId)?.remainingHours;
      case 'id':
        return p.internalId;
      case 'nome':
        return p.name;
      case 'categoria':
        return rangoCategoriaCra(p.craCategory);
      case 'creatoDa':
        return p.createdBy || undefined;
      case 'creatoIl':
        return p.createdAt ? new Date(p.createdAt).getTime() : undefined;
    }
  };
  // Ordine di default (DEFAULT_SORT). Quando l'utente ordina, i suoi criteri vengono prima e
  // questo resta come spareggio deterministico.
  const confrontoDefault = buildComparator(DEFAULT_SORT, valoreOrdinamento);
  // Criteri mostrati nelle intestazioni: senza ordinamento utente, quelli di default.
  const criteriVisibili = ordinamento.length > 0 ? ordinamento : DEFAULT_SORT;
  const confrontoUtente = buildComparator(ordinamento, valoreOrdinamento);
  const prodottiOrdinati =
    prodotti === undefined
      ? undefined
      : [...prodotti].sort((a, b) => confrontoUtente(a, b) || confrontoDefault(a, b));

  // Filtro "solo prodotti con CVE": quando attivo mostra solo i prodotti con almeno una
  // vulnerabilità aperta (presenti nel set dei productIds impattati).
  const prodottiFiltrati =
    prodottiOrdinati === undefined
      ? undefined
      : soloCve
        ? prodottiOrdinati.filter((p) => openSets?.productIds.has(p.internalId) ?? false)
        : prodottiOrdinati;

  /** Colore del countdown SLA in base alle ore rimanenti (rosso se scaduto, arancio se <8h). */
  function coloreSla(remainingHours: number): string {
    if (remainingHours <= 0) return '#c0392b';
    if (remainingHours < 8) return '#e67e22';
    return '#495057';
  }

  /** Etichetta della fase CRA Art.14 per il tooltip. */
  function labelFase(phase: 'early' | 'vulnerability'): string {
    return phase === 'vulnerability' ? 'Notifica 72h' : 'Preavviso 24h';
  }

  function toggleOrdinamento(key: ProductSortKey) {
    setOrdinamento((prev) => toggleSort(prev, key));
  }

  /**
   * Elimina (soft-delete) un prodotto. Prima chiede conferma: se il prodotto ha forniture
   * tracciate (su una qualsiasi delle sue release), il messaggio lo segnala esplicitamente con il
   * numero di forniture, per evitare cancellazioni accidentali di prodotti con clienti reali collegati.
   */
  async function eliminaProdotto(p: Product) {
    setErroreEliminazione(null);

    let forniture: number;
    try {
      forniture = (await fetchDeployments(p.internalId)).length;
    } catch {
      // Best-effort: se il check forniture fallisce, procede comunque con una conferma generica
      // (non deve bloccare l'eliminazione per un errore di rete sul solo controllo preventivo).
      forniture = 0;
    }

    const messaggioBase = `Eliminare il prodotto "${p.name}"?`;
    const messaggioForniture =
      forniture > 0
        ? `\n\nATTENZIONE — forniture tracciate su questo prodotto:\n- ${p.name}: ${forniture} fornitura/e`
        : '';

    if (!window.confirm(messaggioBase + messaggioForniture)) return;

    setEliminazioneInCorso(true);
    try {
      await deleteProduct.mutateAsync(p.internalId);
    } catch (err) {
      setErroreEliminazione(
        err instanceof ApiError ? err.message : `Errore eliminando ${p.internalId}`,
      );
    } finally {
      setEliminazioneInCorso(false);
    }
  }

  return (
    <div data-testid="product-list-page">
      <div className={styles.listHeader}>
        <h1 className={styles.pageTitle}>Prodotti</h1>
        {/* Apri creazione prodotto — solo per chi ha PORTFOLIO_MANAGEMENT */}
        {puoGestire && (
          <button
            type="button"
            className={styles.btnPrimary}
            onClick={() => setModaleAperta(true)}
            data-testid="btn-nuovo-prodotto"
          >
            + Nuovo prodotto
          </button>
        )}
      </div>

      {/* Modal di creazione prodotto */}
      {puoGestire && modaleAperta && (
        <Modal title="Nuovo prodotto" onClose={() => setModaleAperta(false)}>
          <NuovoProdottoForm onCreato={() => setModaleAperta(false)} />
        </Modal>
      )}

      {erroreEliminazione && (
        <p className={styles.errorMsg} data-testid="errore-eliminazione-prodotto">
          {erroreEliminazione}
        </p>
      )}

      {/* Filtro: mostra solo i prodotti con CVE aperte */}
      {!isLoading && !isError && prodotti !== undefined && prodotti.length > 0 && (
        <div className={styles.formRow} style={{ marginBottom: '0.75rem', alignItems: 'center' }}>
          <label
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}
          >
            <input
              type="checkbox"
              checked={soloCve}
              onChange={(e) => setSoloCve(e.target.checked)}
              data-testid="toggle-solo-cve"
            />
            Solo prodotti con CVE
          </label>
          {ordinamento.length > 0 && (
            <button
              type="button"
              className={`${styles.btnSecondary} ${styles.btnSmall}`}
              onClick={() => setOrdinamento([])}
              data-testid="btn-azzera-ordinamento"
            >
              Default sort
            </button>
          )}
        </div>
      )}

      {/* Tabella prodotti */}
      {isLoading && (
        <p className={styles.loadingMsg} data-testid="loading-prodotti">
          Caricamento prodotti…
        </p>
      )}

      {isError && (
        <p className={styles.errorMsg} data-testid="errore-prodotti">
          {error instanceof Error ? error.message : 'Errore nel caricamento dei prodotti'}
        </p>
      )}

      {!isLoading && !isError && prodotti !== undefined && prodotti.length === 0 && (
        <div className={styles.emptyState} data-testid="lista-vuota">
          Nessun prodotto registrato.{' '}
          {puoGestire ? 'Usa il pulsante “Nuovo prodotto” per crearne uno.' : ''}
        </div>
      )}

      {!isLoading &&
        !isError &&
        prodottiFiltrati !== undefined &&
        prodottiFiltrati.length === 0 &&
        (prodotti?.length ?? 0) > 0 && (
          <div className={styles.emptyState} data-testid="lista-filtrata-vuota">
            Nessun prodotto con CVE aperte corrisponde al filtro.
          </div>
        )}

      {!isLoading && !isError && prodottiFiltrati !== undefined && prodottiFiltrati.length > 0 && (
        <table className={styles.table} data-testid="tabella-prodotti">
          <thead>
            <tr>
              <th className={styles.colIcona}>
                <span className={styles.srOnly}>Prodotto impattato</span>
              </th>
              <th className={styles.colNum}>
                <IntestazioneOrdinabile
                  sortKey="vuln"
                  label="#vuln"
                  criteria={criteriVisibili}
                  onToggle={toggleOrdinamento}
                />
              </th>
              <th className={styles.colSla}>
                <IntestazioneOrdinabile
                  sortKey="sla"
                  label="Prossima scadenza SLA"
                  criteria={criteriVisibili}
                  onToggle={toggleOrdinamento}
                />
              </th>
              <th className={styles.colId}>
                <IntestazioneOrdinabile
                  sortKey="id"
                  label="ID interno"
                  criteria={criteriVisibili}
                  onToggle={toggleOrdinamento}
                />
              </th>
              <th>
                <IntestazioneOrdinabile
                  sortKey="nome"
                  label="Nome"
                  criteria={criteriVisibili}
                  onToggle={toggleOrdinamento}
                />
              </th>
              <th className={styles.colCategoria}>
                <IntestazioneOrdinabile
                  sortKey="categoria"
                  label="Categoria CRA"
                  criteria={criteriVisibili}
                  onToggle={toggleOrdinamento}
                />
              </th>
              <th className={styles.colCreatoDa}>
                <IntestazioneOrdinabile
                  sortKey="creatoDa"
                  label="Creato da"
                  criteria={criteriVisibili}
                  onToggle={toggleOrdinamento}
                />
              </th>
              <th className={styles.colData}>
                <IntestazioneOrdinabile
                  sortKey="creatoIl"
                  label="Creato il"
                  criteria={criteriVisibili}
                  onToggle={toggleOrdinamento}
                />
              </th>
              {puoGestire && <th className={styles.colAzioni}>Azioni</th>}
            </tr>
          </thead>
          <tbody>
            {prodottiFiltrati.map((p) => {
              const impattato = eImpattato(p.internalId);
              const nVulnerabilita = vulnerabilita(p.internalId);
              const sla = slaMap?.get(p.internalId);
              return (
                <tr
                  key={p.internalId}
                  className={impattato ? styles.rowImpattata : undefined}
                  data-testid={`riga-prodotto-${p.internalId}`}
                >
                  <td
                    className={styles.colIcona}
                    data-testid={`impattato-${p.internalId}`}
                    data-impattato={impattato}
                  >
                    {impattato && (
                      <span
                        className={styles.iconaImpattato}
                        tabIndex={0}
                        role="img"
                        aria-label="Prodotto impattato"
                        data-testid={`impattato-icona-${p.internalId}`}
                      >
                        <svg width="18" height="18" viewBox="0 0 12 12" aria-hidden="true">
                          <path
                            d="M6 1.2 11 10.4H1z"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.2"
                            strokeLinejoin="round"
                          />
                          <path d="M6 4.6v2.7M6 8.6v.1" stroke="currentColor" strokeWidth="1.3" />
                        </svg>
                        <span className={styles.tooltipImpattato} aria-hidden="true">
                          Prodotto impattato
                        </span>
                      </span>
                    )}
                  </td>
                  <td className={styles.colNum}>
                    {nVulnerabilita > 0 ? (
                      <span
                        className={`${styles.badge} ${styles.badgeOpenVuln ?? ''}`}
                        title={`${nVulnerabilita} vulnerabilità aperte da gestire`}
                        data-testid={`badge-vuln-${p.internalId}`}
                      >
                        {nVulnerabilita}
                      </span>
                    ) : (
                      <span style={{ color: '#adb5bd' }} data-testid={`badge-vuln-${p.internalId}`}>
                        0
                      </span>
                    )}
                  </td>
                  <td className={styles.colSla}>
                    {sla === undefined ? (
                      <span style={{ color: '#adb5bd' }} data-testid={`sla-${p.internalId}`}>
                        —
                      </span>
                    ) : (
                      <span
                        style={{ color: coloreSla(sla.remainingHours), fontWeight: 600 }}
                        title={`${labelFase(sla.phase)} — ore rimanenti alla scadenza`}
                        data-testid={`sla-${p.internalId}`}
                      >
                        {formatSlaHours(sla.remainingHours)}
                      </span>
                    )}
                  </td>
                  <td className={styles.colId}>
                    <button
                      className={styles.linkCell}
                      title={p.internalId}
                      onClick={() => navigate(`/products/${p.internalId}`)}
                      data-testid={`link-prodotto-${p.internalId}`}
                    >
                      {p.internalId}
                    </button>
                  </td>
                  <td>
                    {p.name}
                    {/* Pending-scan: il prodotto ha ≥1 bundle modificato dopo l'ultima scansione. */}
                    {pendingSets?.productIds.has(p.internalId) && (
                      <>
                        {' '}
                        <PendingScanBadge testId={`product-pending-${p.internalId}`} />
                      </>
                    )}
                  </td>
                  <td className={styles.colCategoria} data-testid={`cra-category-${p.internalId}`}>
                    {(() => {
                      const cat = p.craCategory ?? 'default';
                      const labelMap: Record<CraCategory, string> = {
                        critical: 'Critical',
                        important: 'Important',
                        default: 'Default',
                      };
                      return labelMap[cat];
                    })()}
                  </td>
                  <td
                    className={styles.colCreatoDa}
                    title={p.createdBy}
                    data-testid={`creato-da-${p.internalId}`}
                  >
                    {p.createdBy ?? '—'}
                  </td>
                  <td className={styles.colData} data-testid={`creato-il-${p.internalId}`}>
                    {p.createdAt ? (
                      <span title={formattaDataOra(p.createdAt)}>{formattaData(p.createdAt)}</span>
                    ) : (
                      '—'
                    )}
                  </td>
                  {puoGestire && (
                    <td className={styles.colAzioni}>
                      <button
                        type="button"
                        className={`${styles.btnDanger} ${styles.btnSmall} ${styles.btnIcon}`}
                        onClick={() => void eliminaProdotto(p)}
                        disabled={eliminazioneInCorso}
                        aria-label={`Elimina ${p.name}`}
                        title="Elimina"
                        data-testid={`btn-elimina-prodotto-${p.internalId}`}
                      >
                        <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
                          <path
                            d="M2.5 4h11M6 4V2.5h4V4M4 4l.7 9.2c0 .5.4.8.8.8h5c.4 0 .8-.3.8-.8L12 4M6.6 6.5v5M9.4 6.5v5"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
